import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Briefcase,
  Building2,
  Factory,
  IdCard,
  Mail,
  Pencil,
  Save,
  Shield,
  Square,
  SquareCheck,
  User,
  X,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Avatar } from "../ds";
import { useAuth } from "../context/AuthContext";
import { useCurrentUserProfile } from "../hooks/useUserProfile";
import { useDepartmentNames } from "../hooks/useDepartments";
import { useRegisterTopBarActions } from "../context/TopBarActionsContext";
import { updateUserProfile } from "../api/auth";
import { apiErrorMessage } from "../utils/apiError";
import { buildUserUnitOptions } from "../utils/departments";

// Field block — uppercase caption + icon-prefixed value below.
// Mirrors the screenshot's "icon + text" pattern. Pass a ReactNode as
// `editor` to render an inline edit control (Autocomplete, TextField)
// in place of the static value.
function Field({
  label,
  icon,
  value,
  editor,
}: {
  label: string;
  icon: ReactNode;
  value?: string | null;
  editor?: ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Field labels use the Raven DS overline variant (12/500/2px/
        32lh, uppercase) so the section headings stay consistent with
        the rest of the app's tile/card labels. */}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.4, mb: 0.5 }}
      >
        {label}
      </Typography>
      {editor ?? (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              color: "text.secondary",
              display: "inline-flex",
              "& svg": { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: value ? "text.primary" : "text.disabled",
            }}
          >
            {value || "—"}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

const EMPLOYEE_TYPES = ["Permanent", "Contract", "Temporary", "Intern"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];

export default function Profile() {
  const { auth, userName, userRole } = useAuth();
  const { data: profile } = useCurrentUserProfile();
  const info = profile?.user_info ?? auth?.user.user_info ?? null;
  // Prefer the live profile's role over the value cached in localStorage
  // at login time. Stored auth can be stale (e.g. role assigned/changed
  // after the user last logged in) — `info` follows the same pattern.
  const role = profile?.role || userRole;

  const currentUnit = info?.units?.[0] ?? info?.unit ?? "";

  const { names: allDepartments, departments: deptRaw } = useDepartmentNames();
  const derivedUnits = buildUserUnitOptions(deptRaw);
  // Keep the saved unit selectable even when no department carries it yet.
  const allUnits =
    currentUnit && !derivedUnits.includes(currentUnit)
      ? [...derivedUnits, currentUnit].sort((a, b) => a.localeCompare(b))
      : derivedUnits;

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<{
    unit: string;
    departments: string[];
    employee_type: string;
    gender: string;
  }>({
    unit: currentUnit,
    departments: info?.departments ?? [],
    employee_type: info?.employee_type ?? "",
    gender: info?.gender ?? "",
  });

  // Dirty check — only enable Save when something Save persists changed.
  // Employee Type is not persisted: its options do not match the
  // backend's EmployeeType values.
  const isDirty =
    draft.unit !== currentUnit ||
    draft.gender !== (info?.gender ?? "") ||
    draft.departments.length !== (info?.departments ?? []).length ||
    draft.departments.some(
      (d, i) => d !== (info?.departments ?? [])[i],
    );

  // Save/Cancel live at the bottom of the card while editing; Edit
  // lives in the in-card header otherwise. No top-bar actions needed.
  useRegisterTopBarActions(null);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const userId = auth?.user.id;
  const saveMutation = useMutation({
    mutationFn: () =>
      updateUserProfile(userId as string, {
        units: [draft.unit],
        departments: draft.departments,
        ...(draft.gender ? { gender: draft.gender } : {}),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user-profile", userId] }),
        queryClient.invalidateQueries({ queryKey: ["ess-profile-gate"] }),
      ]);
      setEditMode(false);
      enqueueSnackbar("Profile saved", { variant: "success" });
    },
    onError: (err) =>
      enqueueSnackbar(apiErrorMessage(err, "Could not save your profile"), {
        variant: "error",
      }),
  });
  const unitMissing = draft.unit.trim().length === 0;

  const startEdit = () => {
    setDraft({
      unit: currentUnit,
      departments: info?.departments ?? [],
      employee_type: info?.employee_type ?? "",
      gender: info?.gender ?? "",
    });
    setEditMode(true);
  };

  const departments = info?.departments ?? [];
  const subtitle = [
    role,
    departments[0],
    currentUnit,
  ]
    .filter(Boolean)
    .join(" · ");

  const isEssVerified = profile?.user_info?.ess_verified === true;

  return (
    <Box sx={{ maxWidth: 670, mx: "auto" }}>
      {!isEssVerified && (
        <Alert
          severity="info"
          icon={false}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            border: (t) => `1px solid ${t.palette.info.main}33`,
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            Pending admin verification
          </AlertTitle>
          Your ESS access is awaiting admin approval. Once verified, sign in
          again to enter the application. You can update your profile while
          you wait.
        </Alert>
      )}
      <Card>
        <CardContent sx={{ p: 3 }}>
          {/* Header strip — Avatar | name + subtitle | Edit. The Edit
            button hides while editing, but the name + avatar stay
            visible so the user keeps their identity context (name is
            assigned by admin and not editable here). */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2.5}
            sx={{ mb: 3 }}
          >
            <Avatar
              size="large"
              gradient
              variant="rounded"
              sx={{
                width: 64,
                height: 64,
                fontSize: 22,
                fontWeight: 700,
                borderRadius: "8px",
                filter: "grayscale(1)",
              }}
            >
              {(() => {
                // Initials = first letter of the first 2 space-separated
                // tokens. "Manikandan" → "M", "rishi admin" → "RA".
                const tokens = (userName || "?")
                  .split(/\s+/)
                  .filter(Boolean);
                if (tokens.length === 0) return "?";
                if (tokens.length === 1) return tokens[0].charAt(0).toUpperCase();
                return (
                  tokens[0].charAt(0) + tokens[tokens.length - 1].charAt(0)
                ).toUpperCase();
              })()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Inherits Raven DS h5 (24/500/0.15px/32lh) — no
                fontWeight override so the page typography matches the
                theme. */}
              <Typography
                variant="h5"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={userName || auth?.user.email || ""}
              >
                {userName || auth?.user.email || "—"}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {!editMode && (
              <Button
                variant="text"
                startIcon={<RavenIcon icon={Pencil} />}
                onClick={startEdit}
                sx={{
                  flexShrink: 0,
                  color: "primary.dark",
                }}
              >
                Edit
              </Button>
            )}
          </Stack>

          <Divider sx={{ mb: 3, mx: -3 }} />

          {/* Personal block — 2-col in view mode, single column when editing */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: editMode
                ? "1fr"
                : { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 4,
              rowGap: 3,
              mb: 3,
            }}
          >
            <Field
              label="Employee ID"
              icon={<RavenIcon icon={IdCard} />}
              value={info?.employee_id}
              editor={
                editMode ? (
                  <TextField
                    size="small"
                    value={info?.employee_id ?? ""}
                    disabled
                    fullWidth
                    helperText="Assigned by admin"
                  />
                ) : undefined
              }
            />
            <Field
              label="Email"
              icon={<RavenIcon icon={Mail} />}
              value={auth?.user.email}
              editor={
                editMode ? (
                  <TextField
                    size="small"
                    value={auth?.user.email ?? ""}
                    disabled
                    fullWidth
                    helperText="Assigned by admin"
                  />
                ) : undefined
              }
            />
            <Field
              label="Employee Type"
              icon={<RavenIcon icon={Briefcase} />}
              value={info?.employee_type ?? null}
              editor={
                editMode ? (
                  <Autocomplete
                    size="small"
                    options={EMPLOYEE_TYPES}
                    value={draft.employee_type || null}
                    onChange={(_e, v) =>
                      setDraft((d) => ({ ...d, employee_type: v ?? "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select type" />
                    )}
                  />
                ) : undefined
              }
            />
            <Field
              label="Gender"
              icon={<RavenIcon icon={User} />}
              value={info?.gender ?? null}
              editor={
                editMode ? (
                  <Autocomplete
                    size="small"
                    options={GENDERS}
                    value={draft.gender || null}
                    onChange={(_e, v) =>
                      setDraft((d) => ({ ...d, gender: v ?? "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select gender" />
                    )}
                  />
                ) : undefined
              }
            />
          </Box>

          <Divider sx={{ mb: 3, mx: -3 }} />

          {/* Org block — Departments + Unit + Role. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: editMode
                ? "1fr"
                : { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 4,
              rowGap: 3,
            }}
          >
            <Field
              label="Departments"
              icon={<RavenIcon icon={Building2} />}
              value={departments.length ? departments.join(", ") : null}
              editor={
                editMode ? (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={allDepartments}
                    value={draft.departments}
                    onChange={(_e, v) =>
                      setDraft((d) => ({ ...d, departments: v }))
                    }
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } =
                        props as typeof props & { key: string };
                      return (
                        <li key={key} {...optionProps}>
                          <Checkbox
                            icon={
                              <RavenIcon icon={Square} />
                            }
                            checkedIcon={
                              <RavenIcon icon={SquareCheck} />
                            }
                            sx={{ mr: 1, p: 0.5 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      );
                    }}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          draft.departments.length === 0
                            ? "Select departments"
                            : ""
                        }
                      />
                    )}
                  />
                ) : undefined
              }
            />
            <Field
              label="Unit"
              icon={<RavenIcon icon={Factory} />}
              value={currentUnit}
              editor={
                editMode ? (
                  <Autocomplete
                    size="small"
                    options={allUnits}
                    value={draft.unit || null}
                    onChange={(_e, v) =>
                      setDraft((d) => ({ ...d, unit: v ?? "" }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={unitMissing}
                        helperText={unitMissing ? "Unit is required" : undefined}
                        placeholder="Select unit"
                        inputProps={{
                          ...params.inputProps,
                          "aria-label": "Unit",
                        }}
                      />
                    )}
                  />
                ) : undefined
              }
            />
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Field
                label="Role"
                icon={<RavenIcon icon={Shield} />}
                value={role}
                editor={
                  editMode ? (
                    <TextField
                      size="small"
                      value={role ?? ""}
                      disabled
                      fullWidth
                      helperText="Assigned by admin"
                    />
                  ) : undefined
                }
              />
            </Box>
          </Box>

          {editMode && (
            <>
              <Divider sx={{ mt: 3, mb: 2.5, mx: -3 }} />
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
              >
                <Button
                  variant="text"
                  startIcon={<RavenIcon icon={X} />}
                  onClick={() => setEditMode(false)}
                  disabled={saveMutation.isPending}
                  sx={{ borderRadius: 999 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RavenIcon icon={Save} />}
                  disabled={
                    !isDirty || unitMissing || !userId || saveMutation.isPending
                  }
                  onClick={() => saveMutation.mutate()}
                  sx={{ borderRadius: 999 }}
                >
                  Save
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
